import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import './CartPage.css';

const CartPage = () => {
  const {
    cart, updateItem, removeItem, clearCart,
    totalPrice, setCheckoutDrawerOpen,
  } = useCart();
  const navigate = useNavigate();

  const items = cart?.items || [];
  const subtotal = Number(totalPrice);
  const shipping = subtotal >= 500 ? 0 : (subtotal > 0 ? 90 : 0);
  const total = subtotal + shipping;
  const remaining = Math.max(0, 500 - subtotal);

  const handleCheckout = () => {
    setCheckoutDrawerOpen(true);   // ← CheckoutDrawer open, navigate nahi
  };

  return (
    <>
      <Helmet><title>Cart – Vedonica International</title></Helmet>
      <div className="cart-page page-wrapper">
        <div className="container">

          <h1 className="cart-page__title">Shopping Cart</h1>

          {/* ── Free shipping progress ─────────────────────── */}
          {subtotal > 0 && (
            <div className="cart-page__freeship">
              <div className="cart-page__freeship-track">
                <div
                  className="cart-page__freeship-fill"
                  style={{ width: `${Math.min((subtotal / 500) * 100, 100)}%` }}
                />
              </div>
              {subtotal >= 500
                ? <p className="cart-page__freeship-text cart-page__freeship-text--done">🎉 Free shipping unlocked!</p>
                : <p className="cart-page__freeship-text">Add <strong>₹{remaining.toFixed(0)}</strong> more for free shipping</p>
              }
            </div>
          )}

          {items.length === 0 ? (
            <div className="cart-page__empty">
              <p className="cart-page__empty-icon">🛒</p>
              <h2>Your cart is empty</h2>
              <p>Add some Vedonica International products to get started!</p>
              <Link to="/" className="btn btn-primary" style={{ marginTop: 24 }}>
                <FiShoppingBag /> Shop Now
              </Link>
            </div>
          ) : (
            <div className="cart-page__layout">

              {/* ── Items column ─────────────────────────── */}
              <div className="cart-page__items">
                <div className="cart-page__items-header">
                  <h2>{items.length} Item{items.length > 1 ? 's' : ''}</h2>
                  <button
                    className="cart-page__clear-btn"
                    onClick={clearCart}
                  >
                    Clear All
                  </button>
                </div>

                {items.map(item => (
                  <div key={item.id} className="cart-page-item">

                    {/* Product image */}
                    {item.product_image && (
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="cart-page-item__img"
                      />
                    )}

                    {/* Content block */}
                    <div className="cart-page-item__content">

                      {/* Top row: name + remove */}
                      <div className="cart-page-item__top">
                        <div className="cart-page-item__meta">
                          <p className="cart-page-item__name">{item.product_name}</p>
                          {item.variant?.pack_label && (
                            <span className="cart-page-item__variant">{item.variant.pack_label}</span>
                          )}
                        </div>
                        {/* Remove — isolated, never overlaps */}
                        <button
                          className="cart-page-item__remove"
                          onClick={() => removeItem(item.id)}
                          aria-label="Remove"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>

                      {/* Bottom row: qty stepper + price */}
                      <div className="cart-page-item__bottom">
                        <div className="cart-page-item__qty">
                          <button
                            onClick={() => {
                              if (item.quantity > 1) updateItem(item.id, item.quantity - 1);
                              else removeItem(item.id);
                            }}
                            aria-label="Decrease"
                          >
                            <FiMinus size={13} />
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            onClick={() => updateItem(item.id, item.quantity + 1)}
                            aria-label="Increase"
                          >
                            <FiPlus size={13} />
                          </button>
                        </div>
                        <p className="cart-page-item__price">
                          ₹{Number(item.subtotal).toFixed(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Order Summary sidebar ───────────────── */}
              <div className="cart-page__sidebar">
                <div className="card cart-summary">
                  <h2 className="cart-summary__title">Order Summary</h2>

                  {/* Item breakdown */}
                  <div className="cart-summary__items">
                    {items.map(item => (
                      <div key={item.id} className="cart-summary__item-row">
                        <span className="cart-summary__item-name">
                          {item.product_name}
                          {item.variant?.pack_label ? ` · ${item.variant.pack_label}` : ''}
                          {` ×${item.quantity}`}
                        </span>
                        <span className="cart-summary__item-price">
                          ₹{Number(item.subtotal).toFixed(0)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="divider" style={{ margin: '12px 0' }} />

                  {/* Totals */}
                  <div className="cart-summary__rows">
                    <div className="cart-summary__row">
                      <span>Subtotal ({items.length} item{items.length > 1 ? 's' : ''})</span>
                      <span>₹{subtotal.toFixed(0)}</span>
                    </div>
                    <div className="cart-summary__row">
                      <span>Shipping</span>
                      <span>
                        {shipping === 0
                          ? <span className="cart-summary__free">FREE</span>
                          : `₹${shipping}`
                        }
                      </span>
                    </div>
                    {shipping > 0 && (
                      <p className="cart-summary__ship-note">
                        Add ₹{remaining.toFixed(0)} more for free shipping
                      </p>
                    )}
                  </div>

                  <div className="divider" style={{ margin: '12px 0' }} />

                  <div className="cart-summary__total">
                    <span>Total</span>
                    <span>₹{total.toFixed(0)}</span>
                  </div>

                  {/* Checkout → opens CheckoutDrawer */}
                  <button
                    className="btn btn-primary cart-summary__cta"
                    onClick={handleCheckout}
                  >
                    Proceed to Checkout <FiArrowRight size={15} />
                  </button>

                  <Link to="/" className="cart-summary__continue">
                    ← Continue Shopping
                  </Link>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartPage;